// Inline Edit controller — supports click-to-edit patterns for ticket fields.
// Used by header subject, description block, and change-request text fields.
//
// Usage:
//   <div data-controller="support--inline-edit">
//     <div data-support--inline-edit-target="display">...</div>
//     <form data-support--inline-edit-target="form" class="hidden"
//           data-action="keydown->support--inline-edit#onKeydown">
//       <input data-support--inline-edit-target="input" ...>
//     </form>
//   </div>

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "form", "input"]

  connect() {
    // Ensure form starts hidden; display starts visible
    if (this.hasFormTarget) {
      this.formTarget.classList.add("hidden")
    }
    if (this.hasDisplayTarget) {
      this.displayTarget.classList.remove("hidden")
    }
  }

  // Show edit form, hide display, focus first focusable element
  show(event) {
    event?.preventDefault()

    if (this.hasDisplayTarget) {
      this.displayTarget.classList.add("hidden")
    }
    if (this.hasFormTarget) {
      this.formTarget.classList.remove("hidden")
    }

    // Focus: explicit input target first, otherwise first focusable in form
    if (this.hasInputTarget) {
      this.inputTarget.focus()
    } else if (this.hasFormTarget) {
      const focusable = this.formTarget.querySelector(
        "input:not([type=hidden]), textarea, [contenteditable]",
      )
      focusable?.focus()
    }
  }

  // Hide edit form, show display
  hide(event) {
    event?.preventDefault()

    if (this.hasFormTarget) {
      this.formTarget.classList.add("hidden")
    }
    if (this.hasDisplayTarget) {
      this.displayTarget.classList.remove("hidden")
    }
  }

  // Submit the form programmatically
  submit(event) {
    event?.preventDefault()
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  // Keyboard handler attached via data-action="keydown->support--inline-edit#onKeydown"
  // ESC -> hide; Enter on input (not textarea) -> submit; Ctrl/Cmd+Enter -> submit
  onKeydown(event) {
    if (event.key === "Escape") {
      this.hide(event)
      return
    }

    const isCtrlEnter =
      (event.ctrlKey || event.metaKey) && event.key === "Enter"
    const isEnterOnInput =
      event.key === "Enter" &&
      !event.shiftKey &&
      event.target.tagName === "INPUT"

    if (isCtrlEnter || isEnterOnInput) {
      event.preventDefault()
      this.submit()
    }
  }
}
